import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Copy, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Money } from "@/components/common/Money";
import { useAuth } from "@/hooks/useAuth";
import { useReferralStats } from "@/hooks/useReferral";
import { PLANS } from "@/lib/constants";
import { toast } from "sonner";

export function Referral() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const stats = useReferralStats();
  const code = profile?.referral_code ?? "";
  const link = `${window.location.origin}/welcome?ref__=${code}`;
  const message = t("referral.shareMessage", { code, link });

  function copyLink() {
    navigator.clipboard.writeText(link);
    toast.success(t("common.copied"));
  }

  function shareWA() {
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  }

  return (
    <div className="space-y-4 pt-2 pb-2">
      <header className="flex items-center gap-2">
        <Button asChild variant="ghost" size="icon-sm"><Link to="/"><ArrowLeft className="size-[18px]" /></Link></Button>
        <div className="flex-1 text-center">
          <h1 className="text-base font-semibold tracking-tight leading-none">Parrainage</h1>
          <p className="text-2xs text-muted-foreground mt-0.5">Invite, gagne, répète.</p>
        </div>
        <div className="size-8" />
      </header>

      {/* Code card */}
      <section className="rounded-2xl border border-white/[0.06] bg-card p-6 text-center shadow-card">
        <p className="text-2xs uppercase tracking-wider text-muted-foreground font-medium">Ton code</p>
        <p className="mt-2 text-3xl font-bold tracking-[0.2em] text-primary num">{code}</p>
        <div className="mt-5 grid grid-cols-2 gap-2">
          <Button variant="secondary" onClick={copyLink}><Copy className="size-3.5" /> Copier</Button>
          <Button onClick={shareWA}><Share2 className="size-3.5" /> WhatsApp</Button>
        </div>
      </section>

      {/* Stats */}
      <section className="grid grid-cols-3 gap-2">
        <Stat label="Invités" value={stats.total} />
        <Stat label="Abonnés" value={stats.paid} highlight />
        <Stat label="Gains" value={<Money amount={profile?.referral_earnings ?? 0} compact />} />
      </section>

      {/* Comment ça marche */}
      <section className="rounded-xl border border-white/[0.06] bg-card p-4 space-y-3">
        <h2 className="text-sm font-semibold tracking-tight">Comment ça marche</h2>
        <Step n={1} text="Partage ton code à tes amis" />
        <Step n={2} text="Ils s'inscrivent et souscrivent" />
        <Step n={3} text="Tu reçois un bonus instantané" />
      </section>

      {/* Bonus par plan */}
      <section className="rounded-xl border border-white/[0.06] bg-card divide-y divide-white/[0.06] overflow-hidden">
        <p className="px-4 py-2.5 text-2xs uppercase tracking-wider text-muted-foreground font-medium">
          Bonus par plan
        </p>
        {Object.entries(PLANS).map(([id, p]) => (
          <div key={id} className="flex items-center justify-between px-4 py-2.5">
            <span className="text-sm font-medium">{p.name}</span>
            <span className="text-sm font-semibold text-primary tabular-nums">
              {id === "bigboss" ? "10 % mise" : p.referralBonus > 0 ? <Money amount={p.referralBonus} /> : "—"}
            </span>
          </div>
        ))}
      </section>
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: React.ReactNode; highlight?: boolean }) {
  return (
    <div className={`rounded-xl border bg-card px-3 py-3 ${highlight ? "border-primary/30 bg-primary/[0.04]" : "border-white/[0.06]"}`}>
      <p className="text-2xs uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
      <p className={`mt-1 text-xl font-bold num ${highlight ? "text-primary" : ""}`}>{value}</p>
    </div>
  );
}

function Step({ n, text }: { n: number; text: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="size-6 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center text-primary text-2xs font-semibold tabular-nums shrink-0">
        {n}
      </div>
      <p className="text-sm pt-0.5">{text}</p>
    </div>
  );
}
